import React, { useEffect, useState, useMemo } from "react";
import { Line } from "react-chartjs-2";
import Panel from "../Panel";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend
);

type MixerAnalysisChartProps = {
  panelTitle?: string;
  panelSubtitle?: string;
  powerData: number[];
  powerReconstructionData: number[];
  online?: 0 | 1;
};

const MixerAnalysisChart = (props: MixerAnalysisChartProps) => {
  const [chartData, setChartData] = useState<any>(null);
  const [viewMode, setViewMode] = useState<'recent' | 'fullspan'>('recent');

  // Custom plugin to draw phase backgrounds
  // Using a ref to track current view mode so the plugin always has access to latest value
  const viewModeRef = React.useRef(viewMode);
  const dataLengthRef = React.useRef(props.powerData.length);

  useEffect(() => {
    viewModeRef.current = viewMode;
  }, [viewMode]);

  useEffect(() => {
    dataLengthRef.current = props.powerData.length;
  }, [props.powerData]);

  const phaseBackgroundPlugin = useMemo(() => ({
    id: 'phaseBackground',
    beforeDraw: (chart: any) => {
      const ctx = chart.ctx;
      const chartArea = chart.chartArea;
      const currentMode = viewModeRef.current;
      const currentDataLength = dataLengthRef.current;

      // Skip if chart area is not initialized
      if (!chartArea) return;

      ctx.save();

      if (currentMode === 'fullspan') {
        // Fixed span mode: Phase divisions at 17, 30, and 100
        const totalPoints = 100;
        const phase1End = 17;
        const phase2End = 30;

        // Calculate x positions based on fixed positions
        const phase1Width = (chartArea.right - chartArea.left) * (phase1End / totalPoints);
        const phase2Width = (chartArea.right - chartArea.left) * ((phase2End - phase1End) / totalPoints);

        const transition1X = chartArea.left + phase1Width;
        const transition2X = transition1X + phase2Width;

        // Draw Phase 1 background (0-17)
        ctx.fillStyle = 'rgba(83, 189, 228, 0.08)';
        ctx.fillRect(chartArea.left, chartArea.top, phase1Width, chartArea.bottom - chartArea.top);

        // Draw Phase 2 background
        // Before Phase 1 completes: Phase 2 extends to point 100 (full remaining width)
        // After Phase 1 completes: Phase 2 shrinks to point 30
        ctx.fillStyle = 'rgba(0, 255, 136, 0.05)';
        if (currentDataLength <= phase1End) {
          // Phase 2 extends to the end (18-100)
          ctx.fillRect(transition1X, chartArea.top, chartArea.right - transition1X, chartArea.bottom - chartArea.top);
        } else {
          // Phase 2 is normal width (18-30)
          ctx.fillRect(transition1X, chartArea.top, phase2Width, chartArea.bottom - chartArea.top);
        }

        // Only show Phase 3 background after Phase 1 is complete (data length > 17)
        if (currentDataLength > phase1End) {
          // Draw Phase 3 background (31-100)
          ctx.fillStyle = 'rgba(255, 193, 7, 0.05)';
          ctx.fillRect(transition2X, chartArea.top, chartArea.right - transition2X, chartArea.bottom - chartArea.top);
        }

        // Draw water marker at point 34 (only after Phase 1 is complete)
        if (currentDataLength > phase1End) {
          const waterPoint = 34;
          const waterX = chartArea.left + (chartArea.right - chartArea.left) * (waterPoint / totalPoints);

          // Draw vertical dashed line
          ctx.strokeStyle = 'rgba(0, 191, 255, 0.6)';
          ctx.lineWidth = 2;
          ctx.setLineDash([5, 3]);
          ctx.beginPath();
          ctx.moveTo(waterX, chartArea.top);
          ctx.lineTo(waterX, chartArea.bottom);
          ctx.stroke();
          ctx.setLineDash([]);

          // Draw label
          ctx.font = 'bold 10px sans-serif';
          ctx.fillStyle = '#00bfff';
          ctx.textAlign = 'center';
          ctx.fillText('Water', waterX, chartArea.top - 5);
        }

        // Draw predicted maximum marker at point 42 (only after Phase 1 is complete)
        if (currentDataLength > phase1End) {
          const predictedMaxPoint = 42;
          const predictedMaxX = chartArea.left + (chartArea.right - chartArea.left) * (predictedMaxPoint / totalPoints);

          // Draw vertical dashed line
          ctx.strokeStyle = 'rgba(255, 0, 0, 0.6)';
          ctx.lineWidth = 2;
          ctx.setLineDash([5, 3]);
          ctx.beginPath();
          ctx.moveTo(predictedMaxX, chartArea.top);
          ctx.lineTo(predictedMaxX, chartArea.bottom);
          ctx.stroke();
          ctx.setLineDash([]);

          // Draw label
          ctx.font = 'bold 10px sans-serif';
          ctx.fillStyle = '#ff0000';
          ctx.textAlign = 'center';
          ctx.fillText('Predicted Max', predictedMaxX, chartArea.top - 5);
        }

        // Draw phase divider lines
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
        ctx.lineWidth = 2;
        ctx.setLineDash([10, 5]);

        // Divider after Phase 1
        ctx.beginPath();
        ctx.moveTo(transition1X, chartArea.top);
        ctx.lineTo(transition1X, chartArea.bottom);
        ctx.stroke();

        // Divider after Phase 2 (only show if Phase 1 is complete)
        if (currentDataLength > phase1End) {
          ctx.beginPath();
          ctx.moveTo(transition2X, chartArea.top);
          ctx.lineTo(transition2X, chartArea.bottom);
          ctx.stroke();
        }

        ctx.setLineDash([]);

        // Draw phase labels
        ctx.font = 'bold 11px sans-serif';
        ctx.textAlign = 'left';

        // Phase 1 label
        ctx.fillStyle = '#53bde4';
        ctx.fillText('Phase 1', chartArea.left + 10, chartArea.top + 20);

        // Phase 2 label
        ctx.fillStyle = '#00ff88';
        ctx.fillText('Phase 2', transition1X + 10, chartArea.top + 20);

        // Phase 3 label (only show if Phase 1 is complete)
        if (currentDataLength > phase1End) {
          ctx.fillStyle = '#ffc107';
          ctx.fillText('Phase 3', transition2X + 10, chartArea.top + 20);
        }
      }
      // Recent mode: No phase backgrounds

      ctx.restore();
    }
  }), []);

  useEffect(() => {
    let labels: string[];
    let powerData: number[];
    let powerReconstructionData: number[];

    if (viewMode === 'fullspan') {
      // Full span mode: Always show 100 points
      const TOTAL_POINTS = 100;
      labels = Array.from({ length: TOTAL_POINTS }, (_, i) => i.toString());

      // Pad data arrays to 100 points if needed
      powerData = [...props.powerData];
      powerReconstructionData = [...props.powerReconstructionData];

      // Fill remaining points with null/undefined to show empty space
      while (powerData.length < TOTAL_POINTS) {
        powerData.push(NaN);
      }
      while (powerReconstructionData.length < TOTAL_POINTS) {
        powerReconstructionData.push(NaN);
      }
    } else {
      // Recent mode: Show only available data points
      const totalPoints = Math.max(props.powerData.length, props.powerReconstructionData.length);
      labels = Array.from({ length: totalPoints }, (_, i) => i.toString());
      powerData = props.powerData;
      powerReconstructionData = props.powerReconstructionData;
    }

    setChartData({
      labels,
      datasets: [
        {
          label: "Power (Measured)",
          data: powerData,
          borderColor: "#00ff88",
          backgroundColor: "rgba(0, 255, 136, 0.1)",
          borderWidth: 2,
          fill: false,
          tension: 0.3,
          pointRadius: 0,
          pointHoverRadius: 4,
          spanGaps: false, // Don't connect lines across NaN values
        },
        {
          label: "Power Reconstruction (AI)",
          data: powerReconstructionData,
          borderColor: "#ff6b6b",
          backgroundColor: "rgba(255, 107, 107, 0.1)",
          borderWidth: 2,
          fill: false,
          tension: 0.3,
          pointRadius: 0,
          pointHoverRadius: 4,
          borderDash: [5, 5],
          spanGaps: false, // Don't connect lines across NaN values
        },
      ],
    });
  }, [props.powerData, props.powerReconstructionData, viewMode]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: "top" as const,
        labels: {
          color: "#c5c5c5",
          font: {
            size: 12,
            weight: "500" as const,
          },
          usePointStyle: true,
          padding: 15,
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: "rgba(33, 40, 44, 0.95)",
        titleColor: "#c5c5c5",
        bodyColor: "#c5c5c5",
        borderColor: "#53bde4",
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        callbacks: {
          label: function (context: any) {
            let label = context.dataset.label || "";
            if (label) {
              label += ": ";
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y.toFixed(2) + " W";
            }
            return label;
          },
          title: function (context: any) {
            const index = context[0].dataIndex;
            if (index <= 17) {
              return `Point ${index} (Phase 1)`;
            } else if (index <= 30) {
              return `Point ${index} (Phase 2)`;
            } else if (index <= 100) {
              return `Point ${index} (Phase 3)`;
            } else {
              return `Point ${index}`;
            }
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: "Data Points",
          color: "#c5c5c5",
          font: {
            size: 12,
            weight: "600" as const,
          },
        },
        ticks: {
          color: "#6c757d",
          maxTicksLimit: 10,
          callback: function (value: any, index: number) {
            // Show ticks at key points
            if (index === 0 || index === 17 || index === 30 || index === 100) {
              return value;
            }
            return "";
          },
        },
        grid: {
          color: "rgba(108, 117, 125, 0.1)",
          drawBorder: false,
        },
      },
      y: {
        display: true,
        // Fixed scale for Full Span mode, auto scale for Recent mode
        min: 0,
        max: viewMode === 'fullspan' ? 2500 : undefined,
        title: {
          display: true,
          text: "Power (W)",
          color: "#c5c5c5",
          font: {
            size: 12,
            weight: "600" as const,
          },
        },
        ticks: {
          color: "#6c757d",
          callback: function (value: any) {
            return value.toFixed(0) + " W";
          },
        },
        grid: {
          color: "rgba(108, 117, 125, 0.2)",
          drawBorder: false,
        },
      },
    },
  };

  const getStatusColor = (status: 0 | 1) => {
    return status === 1 ? "#00ff88" : "#ff3366";
  };

  const getStatusText = (status: 0 | 1) => {
    return status === 1 ? "AI ASSISTED" : "OFFLINE";
  };

  const headerButtons = (
    <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
      {/* View Mode Toggle */}
      <div
        className="view-mode-toggle"
        style={{
          display: "flex",
          background: "rgba(33, 40, 44, 0.8)",
          borderRadius: "8px",
          padding: "4px",
          gap: "4px",
          border: "1px solid rgba(108, 117, 125, 0.3)",
        }}
      >
        <button
          onClick={() => setViewMode('recent')}
          style={{
            padding: "0.5rem 1rem",
            background: viewMode === 'recent'
              ? "linear-gradient(135deg, #53bde4 0%, #4a9bc7 100%)"
              : "transparent",
            border: "none",
            borderRadius: "6px",
            color: viewMode === 'recent' ? "#ffffff" : "#c5c5c5",
            fontSize: "0.75rem",
            fontWeight: "bold",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            cursor: "pointer",
            transition: "all 0.3s ease",
            boxShadow: viewMode === 'recent' ? "0 0 15px rgba(83, 189, 228, 0.4)" : "none",
          }}
          onMouseEnter={(e) => {
            if (viewMode !== 'recent') {
              e.currentTarget.style.backgroundColor = "rgba(83, 189, 228, 0.1)";
            }
          }}
          onMouseLeave={(e) => {
            if (viewMode !== 'recent') {
              e.currentTarget.style.backgroundColor = "transparent";
            }
          }}
        >
          📊 Recent Data
        </button>
        <button
          onClick={() => setViewMode('fullspan')}
          style={{
            padding: "0.5rem 1rem",
            background: viewMode === 'fullspan'
              ? "linear-gradient(135deg, #00ff88 0%, #00cc6e 100%)"
              : "transparent",
            border: "none",
            borderRadius: "6px",
            color: viewMode === 'fullspan' ? "#ffffff" : "#c5c5c5",
            fontSize: "0.75rem",
            fontWeight: "bold",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            cursor: "pointer",
            transition: "all 0.3s ease",
            boxShadow: viewMode === 'fullspan' ? "0 0 15px rgba(0, 255, 136, 0.4)" : "none",
          }}
          onMouseEnter={(e) => {
            if (viewMode !== 'fullspan') {
              e.currentTarget.style.backgroundColor = "rgba(0, 255, 136, 0.1)";
            }
          }}
          onMouseLeave={(e) => {
            if (viewMode !== 'fullspan') {
              e.currentTarget.style.backgroundColor = "transparent";
            }
          }}
        >
          📈 Full Span
        </button>
      </div>

      {/* Status Badge */}
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "0.5rem",
          padding: "0.25rem 0.75rem",
          borderRadius: "6px",
          fontSize: "0.75rem",
          fontWeight: "bold",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          border: `1px solid ${getStatusColor(props.online || 0)}`,
          backgroundColor: `${getStatusColor(props.online || 0)}20`,
          color: getStatusColor(props.online || 0),
        }}
      >
        <div
          style={{
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            backgroundColor: getStatusColor(props.online || 0),
            boxShadow: `0 0 8px ${getStatusColor(props.online || 0)}`,
          }}
        />
        {getStatusText(props.online || 0)}
      </div>
    </div>
  );

  return (
    <Panel
      title={props.panelTitle}
      subtitle={props.panelSubtitle}
      className="panel-css mixer-analysis-panel"
      buttons={headerButtons}
    >
      <div className="mixer-analysis-card">

        {/* Chart */}
        {chartData && (
          <div style={{ height: "500px", width: "100%", position: "relative" }}>
            <Line data={chartData} options={options} plugins={[phaseBackgroundPlugin]} />
          </div>
        )}

        {/* View Mode Info */}
        <div
          style={{
            marginTop: "1rem",
            padding: "0.75rem 1rem",
            background: "rgba(83, 189, 228, 0.05)",
            borderRadius: "8px",
            border: "1px solid rgba(83, 189, 228, 0.2)",
            fontSize: "0.8rem",
            color: "#c5c5c5",
          }}
        >
          {viewMode === 'recent' ? (
            <>
              <strong style={{ color: "#53bde4" }}>Recent Data Mode:</strong> Showing last {Math.max(props.powerData.length, props.powerReconstructionData.length)} data points received. Auto-scaling view for real-time monitoring.
            </>
          ) : (
            <>
              <strong style={{ color: "#00ff88" }}>Full Span Mode:</strong> Displaying fixed 100-point timeline with phase indicators. Phase 1 (0-17), Phase 2 (18-30), and Phase 3 (31-100). Data fills in as it arrives.
            </>
          )}
        </div>

        {/* Phase Legend */}
        <div
          style={{
            display: "flex",
            gap: "2rem",
            marginTop: "1rem",
            padding: "1rem",
            background: "rgba(33, 40, 44, 0.5)",
            borderRadius: "8px",
            border: "1px solid rgba(108, 117, 125, 0.2)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <div
              style={{
                width: "16px",
                height: "16px",
                background: "rgba(83, 189, 228, 0.3)",
                border: "2px solid #53bde4",
                borderRadius: "3px",
              }}
            />
            <span style={{ fontSize: "0.85rem", color: "#c5c5c5" }}>
              <strong style={{ color: "#53bde4" }}>Phase 1:</strong> Points 0-17
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <div
              style={{
                width: "16px",
                height: "16px",
                background: "rgba(0, 255, 136, 0.2)",
                border: "2px solid #00ff88",
                borderRadius: "3px",
              }}
            />
            <span style={{ fontSize: "0.85rem", color: "#c5c5c5" }}>
              <strong style={{ color: "#00ff88" }}>Phase 2:</strong> Points 18-30
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <div
              style={{
                width: "16px",
                height: "16px",
                background: "rgba(255, 193, 7, 0.2)",
                border: "2px solid #ffc107",
                borderRadius: "3px",
              }}
            />
            <span style={{ fontSize: "0.85rem", color: "#c5c5c5" }}>
              <strong style={{ color: "#ffc107" }}>Phase 3:</strong> Points 31-100
            </span>
          </div>
        </div>
      </div>
    </Panel>
  );
};

export default MixerAnalysisChart;
